import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bildirimOlayAdi,
  GUC_ADI,
  GUVENILIR_CIHAZ_GUN,
  PERSONEL_EN_AZ,
  sifreGucu,
} from "@koclukpanel/contracts";
import {
  Alan,
  BosDurum,
  Buton,
  cn,
  HazirAvatar,
  hazirAvatarAdi,
  Iskelet,
  Kart,
  MetinAlani,
  OnayKutusu,
  Rozet,
  SifreDegistirFormu,
  Tablo,
} from "@koclukpanel/ui";
import { SayfaBasligiEki } from "./Kabuk";
import {
  ApiHatasi,
  sifreDegistir,
  bildirimHepsiOkundu,
  bildirimOlaylari,
  bildirimTercihi,
  avatarim,
  avatarFotoYukle,
  avatarimFotoBaglantisi,
  bildirimTercihlerim,
  digerOturumlariSonlandir,
  guvenilirCihazKaldir,
  guvenilirCihazlar,
  guvenilirCihazlariKaldir,
  hazirAvatarSayisi,
  hazirAvatarSec,
  kendiProfilim,
  kendiProfilimiGuncelle,
  ikiAsamaDurum,
  ipKisitiGetir,
  ipKisitiYaz,
  ikiAsamaKapat,
  ikiAsamaKurulumBaslat,
  ikiAsamaKurulumTamamla,
  oturumSonlandir,
  oturumlarim,
  sudoDurum,
  sudoKapat,
  yedekKodYenile,
  type AvatarGorunumu,
  type IkiAsamaDurumu,
  type OturumOzeti,
} from "./api";

/**
 * HESABIM — G-SES-03/04/06, G-AUT-01/02
 *
 * ================================================================
 * BÜTÜN GÜVENLİK MEKANİZMALARI VARDI, HİÇBİRİNİN KAPISI YOKTU
 * ================================================================
 * Oturum listesi, oturum sonlandırma, 2FA kurulumu, yedek kod
 * yenileme, sudo penceresini kapatma — hepsi sunucuda yazılıydı ve
 * testleri geçiyordu. Panelde HİÇBİRİNE ulaşılamıyordu.
 *
 * Bu, "eksik ekran"dan daha kötü bir durum: telefonunu kaybeden
 * kullanıcı sistemde bir düğme bulamadığı için hiçbir şey yapamıyor,
 * ama kod tarafında "oturumlarını kapatabiliyor" yazıyordu.
 *
 * ================================================================
 * MENÜDE İZNE BAĞLI DEĞİL
 * ================================================================
 * Kendi hesabının güvenliği bir izne bağlanamaz. En yetkisiz
 * kullanıcının bile açık oturumlarını görmesi ve kapatması gerekir —
 * hatta EN ÇOK onun gerekir, çünkü yardım isteyeceği kimse yok.
 */

const KURULUM_ADIMI = { KAPALI: 0, QR: 1, KODLAR: 2 } as const;

function tarihSaat(d: string): string {
  return new Date(d).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
}

/**
 * Etiketi olmayan kayıt için TEK cümle — oturumlarda da güvenilir
 * cihazlarda da aynısı. İki ayrı metin, iki ayrı gerçek varmış gibi
 * okunurdu.
 */
const ETIKETSIZ_CIHAZ = "Cihaz kaydedilmemiş";

/**
 * ================================================================
 * TÜRETME KALDIRILDI — ÖLÇÜLDÜ: HEM ERİŞİLEMEZ HEM AYRIŞMIŞTI
 * ================================================================
 * Bu fonksiyon `deviceLabel` boşsa `userAgent`ten bir ad **türetiyordu**
 * ("Chrome · Windows"). Öğrenci PWA oturumu ölçtü:
 *
 *   UserSession   930 satır · `deviceLabel` NULL olan: **0**
 *   TrustedDevice   5 satır · `label`       NULL olan: **0**
 *
 * Sebebi sunucuda: `session.service.ts:168` oturumu açarken
 * `deviceLabel: cihazAdi(input.userAgent)` yazıyor ve o fonksiyon
 * **hiçbir zaman boş dönmüyor** — kullanıcı dizesi yoksa bile
 * `"Bilinmeyen cihaz"` yazıyor. Yani türetme dalı **erişilemez**.
 *
 * ================================================================
 * VE ERİŞİLSEYDİ YANLIŞ CEVAP VERECEKTİ
 * ================================================================
 * Kural iki evdeydi ve **çoktan ayrışmıştı** (ölçüldü):
 *
 *   Opera kullanıcı dizesi → sunucu: "Opera"   panel: "Chrome"
 *
 * Panel `OPR/` kalıbını hiç tanımıyordu; sıralaması da farklıydı.
 *
 * **Bu ekranda yanlış ad, ad yokluğundan kötüdür.** Listenin tek işi
 * *"bu ben miyim"* sorusunu cevaplatmak; Opera'dan girmiş **yabancı**
 * bir oturumu "Chrome · Windows" diye göstermek, onu **tanıdık**
 * gösterir. Yani tam olarak önlemesi gereken şeyi yapar.
 *
 * ================================================================
 * "BİLİNMEYEN DEME" GEREKÇESİ NEDEN ARTIK GEÇERSİZ
 * ================================================================
 * Eski yorum *"tanınmayan dizede ham metne düşüyor, 'Bilinmeyen'
 * yazmıyor"* diyordu. O gerekçe **türetmenin var olduğu** dünyada
 * anlamlıydı. Türetme kalkınca geriye iki gerçek kalıyor ve ikisi
 * **ayrı**:
 *
 *   sunucu "Bilinmeyen cihaz" YAZMIŞ → giriş anında cihaz tanınamadı
 *   alan NULL                        → kayıt etiketlemeden öncesinden
 *
 * İkincisini birincisiymiş gibi göstermek, ölçülmemiş bir şeyi
 * ölçülmüş göstermek olurdu — bu depodaki `null` ≠ `0` kuralının
 * aynısı.
 *
 * ⚠️ Aynı dosyada güvenilir cihaz listesi zaten `label ?? "Bilinmeyen
 * cihaz"` diyordu; yani panel **kendi içinde de** tutarsızdı — bir
 * yerde "Bilinmeyen" demeyi reddedip öbür yerde diyordu. İkisi
 * birleştirildi.
 */
export function cihazAdi(o: Pick<OturumOzeti, "deviceLabel">): string {
  return o.deviceLabel ?? ETIKETSIZ_CIHAZ;
}

export function Hesabim({ yalnizGuvenlik = false }: { yalnizGuvenlik?: boolean }) {
  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki aciklama="Bilgilerini güncel tut, güvenliğini artır, profilini özelleştir." />
      {/*
        ================================================================
        2FA EN ÜSTTE — ÇİZİMİN SIRASI, VE SEBEBİ VAR
        ================================================================
        `Hesabim.dc.html`in ilk notu: *"2FA BORCU: uyarı değil BORÇ.
        Ekranın en üstünde."* Çizimde profil görseli bölümü hiç yok;
        sıra 2FA → oturumlar → bildirimler → yetki penceresi.

        Önce profil görseli geliyordu: kurulumu ZORUNLU ama
        kurulmamış bir doğrulama, avatar yükleyicinin altında
        kalıyordu. Borcun yeri, ödenmesi gereken şeyin görüldüğü ilk
        yerdir.
      */}
      <IkiAsamaBolumu />
      {/*
        UNİKOÇ "Profilim" düzeni (tarama-1 §3.15): solda Profil Resmi, sağda Bilgilerim ve
        Şifre Güncelle iki sütun; dar ekranda alt alta. 2FA borcu yine EN ÜSTTE (yukarıdaki
        gerekçe) — iki sütunlu blok onun ALTINDA başlıyor. "Kim olduğum" (Bilgilerim) sağ
        sütunun ilk kartı: görselin sütunu solda ama kimlik bilgisi yine ilk okunan kart.
      */}
      {yalnizGuvenlik ? (
        <SifreDegistirBolumu />
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
          <ProfilGorseli />
          <div className="flex min-w-0 flex-col gap-4">
            <ProfilBilgileri />
            {/* G-AUT-08a — ekran "şifrenizi değiştirin" diyordu ama yol yoktu. */}
            <SifreDegistirBolumu />
          </div>
        </div>
      )}
      <GuvenilirCihazlarBolumu />
      <OturumlarBolumu />
      {!yalnizGuvenlik && <BildirimTercihleri />}
      {!yalnizGuvenlik && <IpKisitiBolumu />}
      <SudoBolumu />
    </div>
  );
}

// =====================================================================
// PROFİL BİLGİLERİ
// =====================================================================

/**
 * KİŞİNİN KENDİ AD-SOYAD-TELEFON-ROLÜ.
 *
 * ================================================================
 * EKRANDA HİÇ YOKTU — 13.09.2026'da kullanıcı bildirdi
 * ================================================================
 * `Hesabım` 2FA, cihazlar, oturumlar, bildirimler ve IP kısıtından
 * ibaretti; kişi kendi adını, telefonunu ya da rolünü GÖREMİYOR,
 * değiştiremiyordu. Çizimde de (`Hesabim.dc.html`) profil bölümü
 * yok — yani kod çizimi doğru uygulamıştı, eksik olan çizimdi.
 *
 * ROL SALT OKUNUR: kendi rolünü değiştirebilen biri kendi yetkisini
 * yükseltirdi. E-POSTA DA SALT OKUNUR: hem giriş kimliği hem davet
 * hedefi — değiştirilebilseydi şifre sıfırlama ile hesap başka bir
 * adrese taşınabilirdi. İkisi de gösteriliyor ama düzenlenmiyor;
 * göstermemek "bu bilgi yok" izlenimi verirdi.
 */
function ProfilBilgileri() {
  const qc = useQueryClient();
  const profil = useQuery({ queryKey: ["profilim"], queryFn: kendiProfilim, retry: false });

  const [duzenle, setDuzenle] = useState(false);
  const [ad, setAd] = useState("");
  const [soyad, setSoyad] = useState("");
  const [telefon, setTelefon] = useState("");
  const [bilgi, setBilgi] = useState<string | null>(null);

  const kaydet = useMutation({
    mutationFn: () =>
      kendiProfilimiGuncelle({
        firstName: ad.trim(),
        lastName: soyad.trim(),
        // Bos birakmak "telefonu sil" demek; bos dizgi gondermek
        // dogrulamaya takilirdi.
        phone: telefon.trim() === "" ? null : telefon.trim(),
      }),
    onSuccess: async () => {
      setBilgi("Bilgileriniz güncellendi.");
      setDuzenle(false);
      await qc.invalidateQueries({ queryKey: ["profilim"] });
    },
  });

  if (profil.isPending) return <Iskelet satir={3} />;

  if (profil.isError) {
    return (
      <Kart baslik="Profil bilgilerim">
        <BosDurum
          ton="hata"
          baslik="Bilgiler alınamadı"
          aciklama={profil.error instanceof ApiHatasi ? profil.error.message : undefined}
        />
      </Kart>
    );
  }

  const p = profil.data;

  const duzenlemeyiAc = (): void => {
    setAd(p.firstName);
    setSoyad(p.lastName);
    setTelefon(p.phone ?? "");
    setBilgi(null);
    setDuzenle(true);
  };

  return (
    <Kart
      baslik="Profil bilgilerim"
      aciklama="Adınız veli raporlarında ve öğrencilerinizin ekranlarında görünür."
    >
      <div className="flex flex-col gap-3">
        {!duzenle ? (
          <>
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Satir etiket="Ad soyad" deger={`${p.firstName} ${p.lastName}`} />
              <Satir etiket="Rol" deger={p.roller.length ? p.roller.join(", ") : "—"} />
              <Satir etiket="E-posta" deger={p.email} />
              <Satir etiket="Telefon" deger={p.phone ?? "Girilmemiş"} />
            </dl>
            {bilgi !== null && <p className="text-onay-700 text-sm">{bilgi}</p>}
            <div>
              <Buton gorunum="ikincil" onClick={duzenlemeyiAc}>
                Bilgilerimi düzenle
              </Buton>
            </div>
            {/*
              Neyin DEGISMEDIGI soyleniyor. Yoksa "duzenle"ye basip
              e-postayi bulamayan kullanici, alani aradigini sanip
              ekrani hatali sayar.
            */}
            <p className="text-notr-600 text-xs">
              E-posta ve rol buradan değiştirilemez; e-posta giriş kimliğiniz, rolü yöneticiniz
              belirler.
            </p>
          </>
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (ad.trim() && soyad.trim()) kaydet.mutate();
            }}
          >
            <div className="flex flex-wrap gap-3">
              <Alan etiket="Ad" value={ad} onChange={(e) => setAd(e.target.value)} required />
              <Alan
                etiket="Soyad"
                value={soyad}
                onChange={(e) => setSoyad(e.target.value)}
                required
              />
              <Alan
                etiket="Telefon"
                value={telefon}
                onChange={(e) => setTelefon(e.target.value)}
                yardim="Boş bırakırsanız telefonunuz kaldırılır."
              />
            </div>
            {kaydet.isError && (
              <p role="alert" className="text-hata-700 text-sm font-medium">
                {kaydet.error instanceof ApiHatasi
                  ? kaydet.error.message
                  : "Kaydedilemedi, tekrar deneyin."}
              </p>
            )}
            <div className="flex gap-2">
              <Buton type="submit" disabled={!ad.trim() || !soyad.trim() || kaydet.isPending}>
                {kaydet.isPending ? "Kaydediliyor…" : "Kaydet"}
              </Buton>
              <Buton gorunum="sessiz" type="button" onClick={() => setDuzenle(false)}>
                Vazgeç
              </Buton>
            </div>
          </form>
        )}
      </div>
    </Kart>
  );
}

function Satir({ etiket, deger }: { etiket: string; deger: string }) {
  return (
    <div>
      <dt className="text-notr-600 text-xs">{etiket}</dt>
      <dd className="text-notr-900 text-sm font-medium">{deger}</dd>
    </div>
  );
}

// =====================================================================
// GÜVENİLİR CİHAZLAR — G-AUT-01c
// =====================================================================

/**
 * Kullanıcının "30 gün hatırla" dediği tarayıcılar.
 *
 * ================================================================
 * "AÇIK OTURUMLAR"DAN AYRI BİR BÖLÜM
 * ================================================================
 * İkisi de cihaz listesi ve tek tabloda birleştirmek cazip. Ama
 * apayrı iki şey söylüyorlar:
 *
 *   Açık oturumlar   "şu an nereden GİRİLMİŞ" — kapatınca o cihaz
 *                    dışarı düşer
 *   Güvenilir cihaz  "nerede kod SORULMUYOR" — kaldırınca kimse
 *                    dışarı düşmez, yalnızca bir dahakine kod
 *                    sorulur
 *
 * Tek tabloda olsalardı kullanıcı "kapat"a basıp cihazın çıkmasını
 * beklerdi ve çıkmayınca sistemin bozuk olduğunu düşünürdü.
 *
 * BÖLÜM 2FA'NIN HEMEN ALTINDA: güvenilir cihaz 2FA'yı atlıyor,
 * yani okunması gereken sıra bu.
 */
function GuvenilirCihazlarBolumu() {
  const qc = useQueryClient();
  const liste = useQuery({
    queryKey: ["guvenilir-cihazlar"],
    queryFn: guvenilirCihazlar,
    retry: false,
  });

  const yenile = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ["guvenilir-cihazlar"] });
  };

  // Ucu dogrudan `mutationFn`e vermiyorum — gerekcesi
  // `OturumlarBolumu` icindeki notta.
  const tek = useMutation({
    mutationFn: (id: string) => guvenilirCihazKaldir(id),
    onSuccess: yenile,
  });
  const hepsi = useMutation({
    mutationFn: () => guvenilirCihazlariKaldir(),
    onSuccess: yenile,
  });

  const hata = tek.error ?? hepsi.error ?? null;

  /*
    BÖLÜM HİÇ CİHAZ YOKSA DA ÇİZİLİYOR ve bu bilinçli: kullanıcı
    "acaba bir yerde kod sorulmadan giriliyor mu" sorusunun cevabını
    arıyorsa, bölümün olmaması cevabı vermiyor — yalnızca soruyu
    açık bırakıyor. Boş liste "hayır" demek.
  */
  return (
    <Kart
      baslik="Güvenilir cihazlar"
      aciklama={`Doğrulama kodunun ${GUVENILIR_CIHAZ_GUN} gün boyunca sorulmadığı tarayıcılar. Tanımadığınız bir satır görürseniz kaldırın ve şifrenizi değiştirin.`}
      eylem={
        (liste.data?.length ?? 0) > 0 ? (
          <Buton gorunum="ikincil" yukleniyor={hepsi.isPending} onClick={() => hepsi.mutate()}>
            Hepsini kaldır
          </Buton>
        ) : undefined
      }
    >
      {liste.isPending ? (
        <Iskelet satir={2} />
      ) : liste.isError ? (
        <BosDurum
          ton="hata"
          baslik="Cihazlar alınamadı"
          aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      ) : liste.data.length === 0 ? (
        <BosDurum
          baslik="Güvenilir cihaz yok"
          aciklama="Her girişte doğrulama kodu soruluyor. Bir cihazı hatırlatmak için girişte kod ekranındaki kutuyu işaretleyin."
        />
      ) : (
        <div className="flex flex-col gap-3">
          {hata && (
            <p role="alert" className="text-hata-700 text-sm font-medium">
              {hata instanceof ApiHatasi ? hata.message : "İşlem tamamlanamadı."}
            </p>
          )}

          {hepsi.isSuccess && (
            <p className="text-notr-700 text-sm">
              {hepsi.data.kaldirilan} cihaz kaldırıldı. Bir dahaki girişte kod sorulacak.
            </p>
          )}

          <Tablo
            satirlar={liste.data}
            anahtar={(c) => c.id}
            kartBasligi={0}
            sutunlar={[
              {
                baslik: "Cihaz",
                mobil: true,
                className: "font-medium",
                hucre: (c) => c.label ?? ETIKETSIZ_CIHAZ,
              },
              { baslik: "IP", hucre: (c) => c.ipAddress ?? "—" },
              { baslik: "Eklendi", mobil: true, hucre: (c) => tarihSaat(c.createdAt) },
              {
                baslik: "Son kullanım",
                mobil: true,
                /*
                  "—" değil "Hiç kullanılmadı": boş bir hücre
                  ölçülmemiş mi yoksa sıfır mı belli etmiyor. Bu
                  satırda ayrım önemli — hiç kullanılmamış bir
                  güvenilir cihaz, kullanıcının hatırlamadığı bir
                  işaretleme demek.
                */
                hucre: (c) => (c.lastUsedAt ? tarihSaat(c.lastUsedAt) : "Hiç kullanılmadı"),
              },
              { baslik: "Biter", hucre: (c) => tarihSaat(c.expiresAt) },
              {
                baslik: "İşlem",
                // Dar ekranda da görünüyor — gerekçesi `OturumlarBolumu`.
                mobil: true,
                hucre: (c) => (
                  <div className="flex justify-end">
                    <Buton
                      gorunum="sessiz"
                      yukleniyor={tek.isPending && tek.variables === c.id}
                      onClick={() => tek.mutate(c.id)}
                    >
                      Kaldır
                      <span className="sr-only"> — {c.label ?? ETIKETSIZ_CIHAZ}</span>
                    </Buton>
                  </div>
                ),
              },
            ]}
          />

          <p className="text-notr-600 text-xs">
            Şifrenizi değiştirdiğinizde ya da iki adımlı doğrulamayı kapattığınızda bu listedeki
            cihazların tamamı kendiliğinden düşer.
          </p>
        </div>
      )}
    </Kart>
  );
}

// =====================================================================
// BİLDİRİM TERCİHLERİ — F-NTF-03
// =====================================================================

/**
 * Olay kodları kullanıcıya GÖSTERİLMEZ. Etiketler sözleşme paketindeki
 * tek sözlükten gelir (`bildirimOlayAdi`): panelde ikinci bir harita
 * olunca katalog büyüdükçe biri geride kalıyor ve ekranda ham
 * "OdemeVadesiYaklasti" görünüyordu.
 *
 * Tanınmayan kod listeden DÜŞMÜYOR (kullanıcı kapatamadığı bir
 * bildirimi hiç göremezdi) ve ham kod olarak da görünmüyor: sözlük
 * anlaşılır bir yedek metin üretiyor.
 */
/**
 * KAPATILAMAYAN OLAYLAR.
 *
 * "Hesabına birisi girdi" ve "hesabının görünümüne geçildi"
 * bildirimleri kullanıcıyı KORUMAK için var. Kapatılabilseydi,
 * hesabı ele geçiren kişinin ilk yapacağı şey onu kapatmak olurdu —
 * yani koruma tam ihtiyaç duyulduğu anda yok olurdu.
 */
const KAPATILAMAZ: readonly string[] = ["YeniCihazGirisi", "HesabinizinGorunumuneGecildi"];

function BildirimTercihleri() {
  const qc = useQueryClient();
  const [bilgi, setBilgi] = useState<string | null>(null);

  const olaylar = useQuery({
    queryKey: ["bildirim-olaylar"],
    queryFn: bildirimOlaylari,
    retry: false,
  });
  const tercihler = useQuery({
    queryKey: ["bildirim-tercihler"],
    queryFn: bildirimTercihlerim,
    retry: false,
  });

  const kaydet = useMutation({
    mutationFn: (g: { olay: string; acik: boolean }) => bildirimTercihi(g.olay, "IN_APP", g.acik),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["bildirim-tercihler"] });
    },
  });

  const hepsiOkundu = useMutation({
    mutationFn: bildirimHepsiOkundu,
    onSuccess: (s) =>
      setBilgi(
        s.adet === 0 ? "Okunmamış bildirim yoktu." : `${s.adet} bildirim okundu işaretlendi.`,
      ),
  });

  if (olaylar.isPending || tercihler.isPending) return <Iskelet satir={3} />;

  if (olaylar.isError || tercihler.isError) {
    const hata = olaylar.error ?? tercihler.error;
    return (
      <Kart baslik="Bildirim tercihleri">
        <BosDurum
          ton="hata"
          baslik="Tercihler alınamadı"
          aciklama={hata instanceof ApiHatasi ? hata.message : undefined}
          eylem={
            <Buton
              gorunum="ikincil"
              onClick={() => {
                void olaylar.refetch();
                void tercihler.refetch();
              }}
            >
              Tekrar dene
            </Buton>
          }
        />
      </Kart>
    );
  }

  /*
    KAYDI OLMAYAN OLAY VARSAYILAN AÇIK. Sunucu yalnızca kayıtlı
    tercihleri döndürüyor; listede olmayan bir olayı "kapalı" saymak,
    hiç dokunmamış kullanıcının bütün bildirimlerini kapalı
    göstermek olurdu.
  */
  const durum = new Map(
    tercihler.data.filter((t) => t.channel === "IN_APP").map((t) => [t.eventCode, t.enabled]),
  );
  const acikMi = (olay: string): boolean => durum.get(olay) ?? true;

  return (
    <Kart
      baslik="Bildirim tercihleri"
      aciklama="Panel içi bildirimler. Kapattığınız olay için bildirim üretilmez; olayın kendisi yine kayda geçer."
      eylem={
        <Buton
          gorunum="ikincil"
          yukleniyor={hepsiOkundu.isPending}
          onClick={() => hepsiOkundu.mutate()}
        >
          Hepsini okundu işaretle
        </Buton>
      }
    >
      <div className="flex flex-col gap-2">
        {bilgi && (
          <p role="status" className="text-notr-700 text-sm">
            {bilgi}
          </p>
        )}
        {kaydet.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Tercih kaydedilemedi."}
          </p>
        )}

        <ul className="flex flex-col">
          {olaylar.data.map((olay) => {
            const kilitli = KAPATILAMAZ.includes(olay);
            return (
              <li key={olay} className="border-notr-100 border-b last:border-0">
                {/*
  HAM `<input type="checkbox">` KALDIRILDI. Elle yazilan kutu
  `OnayKutusu`nun sozlesmesinin disinda kaliyordu: odak halkasi,
  `aria-describedby` bagi ve dokunma yuksekligi orada tek yerde
  tanimli. Ozellikle odak: klavyeyle gezen kullanici bu kutularda
  tarayicinin varsayilan halkasini goruyordu, panelin kendi
  `:focus-visible` bicimini degil.
*/}
                <OnayKutusu
                  className="py-1"
                  etiket={
                    <>
                      {bildirimOlayAdi(olay)}
                      {kilitli && (
                        <span className="text-notr-600 ml-2 text-xs">
                          — güvenlik bildirimi, kapatılamaz
                        </span>
                      )}
                    </>
                  }
                  checked={kilitli ? true : acikMi(olay)}
                  disabled={kilitli || kaydet.isPending}
                  onChange={(e) => {
                    setBilgi(null);
                    kaydet.mutate({ olay, acik: e.target.checked });
                  }}
                />
              </li>
            );
          })}
        </ul>

        {/*
          ================================================================
          GEREKCE KODA UYMUYORDU — 04.09.2026
          ================================================================
          Cumle "E-posta, SMS ve WhatsApp kanallari henuz acik degil —
          SAGLAYICI SECILMEDI" diyordu. Olctum: sebep e-posta icin YANLIS.

            `posta.service.ts`  nodemailer + SMTP KURULU; davet ve sifre
                                sifirlama postalari GERCEKTEN gidiyor
            `bildirim.service.ts:181`  `TODO(Faz 3): gercek gonderim
                                worker kuyruguna atilacak (e-posta HAZIR,
                                SMS/WhatsApp saglayicisi Faz 3'te)`

          Yani e-postanin saglayicisi VAR; eksik olan bildirimlerin o
          yola baglanmasi. Yoneticiye "saglayici secilmedi" demek, onu
          olmayan bir isi yapmaya (saglayici secmeye) gonderirdi.

          Cumle artik iki sebebi AYIRIYOR, cunku ikisi ayri is.
        */}
        <p className="text-notr-600 text-xs">
          Şu an yalnızca <strong className="text-notr-700">panel içi</strong> bildirimler
          gönderiliyor. E-posta altyapısı hazır ama bildirimler henüz o yola bağlanmadı; SMS ve
          WhatsApp için ayrıca sağlayıcı seçilmedi.
        </p>
      </div>
    </Kart>
  );
}

// =====================================================================
// AÇIK OTURUMLAR — G-SES-03/06
// =====================================================================

function OturumlarBolumu() {
  const qc = useQueryClient();
  const liste = useQuery({ queryKey: ["oturumlarim"], queryFn: oturumlarim, retry: false });

  const yenile = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ["oturumlarim"] });
  };

  // Ucu DOGRUDAN `mutationFn`e vermiyorum: TanStack ikinci argüman
  // olarak kendi baglamini geciriyor ve o nesne istek gövdesine
  // sizabilir. Sarmalayinca uca yalnizca kimlik gidiyor.
  const tek = useMutation({
    mutationFn: (id: string) => oturumSonlandir(id),
    onSuccess: yenile,
  });
  const digerleri = useMutation({
    mutationFn: () => digerOturumlariSonlandir(),
    onSuccess: yenile,
  });

  const hata = tek.error ?? digerleri.error ?? null;

  /*
    Kapanacak oturum sayısı — `suAnki` OLMAYANLAR. `length - 1`
    yazmadım: o, listede tam olarak bir "bu cihaz" satırı olduğunu
    varsayar. Sunucu bir gün iki oturumu da bu cihaza bağlarsa (aynı
    tarayıcıda iki sekme, yenilenmiş jeton) sayı sessizce yanlış
    olurdu. Sayılan şey, kapatılacak şeyin ta kendisi.
  */
  const kapanacak = (liste.data ?? []).filter((o) => !o.suAnki).length;

  return (
    <Kart
      baslik="Açık oturumlar"
      aciklama="Hesabınıza şu an hangi cihazlardan girilmiş. Tanımadığınız bir satır görürseniz kapatın ve şifrenizi değiştirin."
      eylem={
        kapanacak > 0 ? (
          /*
            ================================================================
            DÜĞME NE YAPACAĞINI ÖNCEDEN SÖYLÜYOR
            ================================================================
            Çizimin notu: *"İKİ EYLEM, İKİ AYRI ŞEY. Yan yana durursa
            karışır."* — ve düğmenin altına şunu yazıyor: "Bu cihaz
            açık kalır · 2 oturum kapanır".

            Önce yalnızca "Diğerlerini kapat" yazıyordu. Kaç oturumun
            kapanacağını kullanıcı ancak **bastıktan sonra**
            öğreniyordu ("2 oturum kapatıldı"), ve bu cihazın açık
            kalacağını hiç öğrenmiyordu. Oysa asıl korku tam orada:
            "bunu basarsam kendimi de atar mıyım?"

            Sayı listeden hesaplanıyor (`suAnki` olmayanlar) — sunucu
            zaten hangi satırın bu cihaz olduğunu söylüyor. Ekran
            tahmin etmiyor.
          */
          <span className="flex flex-col items-end gap-1">
            <Buton
              gorunum="ikincil"
              yukleniyor={digerleri.isPending}
              onClick={() => digerleri.mutate()}
            >
              Diğerlerini kapat
            </Buton>
            <span className="text-notr-600 text-xs">
              Bu cihaz açık kalır · {kapanacak} oturum kapanır
            </span>
          </span>
        ) : undefined
      }
    >
      {liste.isPending ? (
        <Iskelet satir={3} />
      ) : liste.isError ? (
        <BosDurum
          ton="hata"
          baslik="Oturumlar alınamadı"
          aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          {hata && (
            <p role="alert" className="text-hata-500 text-sm font-medium">
              {hata instanceof ApiHatasi ? hata.message : "İşlem tamamlanamadı."}
            </p>
          )}

          {digerleri.isSuccess && (
            <p className="text-notr-700 text-sm">
              {digerleri.data.kapatilan === 0
                ? "Kapatılacak başka oturum yoktu."
                : `${digerleri.data.kapatilan} oturum kapatıldı.`}
            </p>
          )}

          <Tablo
            satirlar={liste.data}
            anahtar={(o) => o.id}
            kartBasligi={0}
            sutunlar={[
              {
                baslik: "Cihaz",
                mobil: true,
                className: "font-medium",
                hucre: (o) => (
                  <span className="flex items-center gap-2">
                    {cihazAdi(o)}
                    {/*
                      "BU CIHAZ" işareti SUNUCUDAN geliyor (`suAnki`).
                      Ekran kendi oturum kimliğiyle karşılaştırma
                      yapamaz: oturum çerezi httpOnly, JavaScript onu
                      okuyamıyor. Tahmin etseydik, yanlış satırı "bu
                      cihaz" diye işaretleyip kullanıcıya KENDİ
                      oturumunu kapattırırdık.
                    */}
                    {o.suAnki && <Rozet ton="onay">Bu cihaz</Rozet>}
                  </span>
                ),
              },
              { baslik: "IP", hucre: (o) => o.ipAddress ?? "—" },
              { baslik: "Giriş", mobil: true, hucre: (o) => tarihSaat(o.createdAt) },
              { baslik: "Son işlem", mobil: true, hucre: (o) => tarihSaat(o.lastSeenAt) },
              {
                baslik: "İşlem",
                /*
                  DAR EKRANDA DA GORUNUYOR. Isaretlemeseydim `Tablo`
                  sutunu telefonda gizlerdi ve ekranin TEK islemi
                  telefonda kaybolurdu — canli denemede (07.08.2026,
                  375px) tam olarak bu oldu: kart "bu cihaz" yaziyor
                  ama tanimadigi oturumu kapatacak dugme yok.
                */
                mobil: true,
                hucre: (o) => (
                  <div className="flex justify-end">
                    {/*
                      KENDİ OTURUMUNDA DÜĞME YOK. Kapatabilseydi bu
                      ekran kullanıcıyı giriş sayfasına atardı ve
                      "çıkış" ile aynı işi yapan ikinci bir düğme
                      olurdu — üstelik adı "sonlandır" olduğu için
                      kullanıcı ne olacağını beklemezdi. Çıkış zaten
                      menünün altında.
                    */}
                    {o.suAnki ? (
                      <span className="text-notr-600 text-sm">—</span>
                    ) : (
                      <Buton
                        gorunum="sessiz"
                        yukleniyor={tek.isPending && tek.variables === o.id}
                        onClick={() => tek.mutate(o.id)}
                      >
                        Kapat
                        <span className="sr-only"> — {cihazAdi(o)}</span>
                      </Buton>
                    )}
                  </div>
                ),
              },
            ]}
          />

          <p className="text-notr-600 text-xs">
            Personel hesabında aynı anda en fazla 3 cihaz açık kalabilir; dördüncü girişte en eski
            oturum kendiliğinden kapanır.
          </p>
        </div>
      )}
    </Kart>
  );
}

// =====================================================================
// İKİ ADIMLI DOĞRULAMA — G-AUT-01, G-AUT-02
// =====================================================================

function IkiAsamaBolumu() {
  const qc = useQueryClient();
  const durum = useQuery({ queryKey: ["2fa-durum"], queryFn: ikiAsamaDurum, retry: false });

  if (durum.isPending) return <Iskelet satir={3} />;

  if (durum.isError) {
    return (
      <Kart baslik="İki adımlı doğrulama">
        <BosDurum
          ton="hata"
          baslik="Durum okunamadı"
          aciklama={durum.error instanceof ApiHatasi ? durum.error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void durum.refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      </Kart>
    );
  }

  const d = durum.data;
  const yenile = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ["2fa-durum"] });
  };

  return (
    <Kart
      baslik="İki adımlı doğrulama"
      aciklama="Şifrenizi bilen ama telefonunuza erişemeyen biri hesabınıza giremez."
      ton={d.kurulumZorunlu && !d.kuruldu ? "uyari" : "notr"}
      eylem={
        d.kuruldu ? (
          <Rozet ton="onay">Kurulu</Rozet>
        ) : (
          <Rozet ton={d.kurulumZorunlu ? "uyari" : "notr"}>Kurulu değil</Rozet>
        )
      }
    >
      {d.kuruldu ? (
        <IkiAsamaKurulu durum={d} yenile={yenile} />
      ) : (
        <IkiAsamaKurulum durum={d} yenile={yenile} />
      )}
    </Kart>
  );
}

function IkiAsamaKurulum({
  durum,
  yenile,
}: {
  durum: IkiAsamaDurumu;
  yenile: () => Promise<void>;
}) {
  const [adim, setAdim] = useState<number>(KURULUM_ADIMI.KAPALI);
  const [kod, setKod] = useState("");

  const baslat = useMutation({
    mutationFn: ikiAsamaKurulumBaslat,
    onSuccess: () => setAdim(KURULUM_ADIMI.QR),
  });
  const tamamla = useMutation({
    mutationFn: () => ikiAsamaKurulumTamamla(kod.trim()),
    onSuccess: async () => {
      setAdim(KURULUM_ADIMI.KODLAR);
      setKod("");
      await yenile();
    },
  });

  return (
    <div className="flex flex-col gap-3">
      {/*
        ================================================================
        SAYAÇ `kurulumZorunlu`UN İÇİNDE DEĞİL — 05.09.2026'da DÜZELTİLDİ
        ================================================================
        POLİTİKA CÜMLESİ SUNUCUDAN gelen alanlardan kuruluyor. Ekran
        "zorunlu mu" kararını KENDİ vermiyor — rolün politikası
        değiştiğinde burada bir şey düzeltmek gerekmesin diye.

        AMA SAYAÇ YANLIŞ YERDEYDİ. Önceki hâlde geçiş sayacı
        `durum.kurulumZorunlu &&` bloğunun İÇİNDE yazılıydı ve o bayrak
        `kurulumZorunluMu` (`two-factor.ts:124`) gereği yalnızca
        `kalan === null || kalan === 0` iken `true` oluyor. Yani:

          · geçiş süresi DEVAM EDERKEN (kalan = 5) bayrak `false`,
            paragraf hiç çizilmiyor — kullanıcı yedi günlük pencereyi
            HİÇ GÖRMÜYORDU;
          · süre DOLUNCA (kalan = 0) çiziliyor ve
            "Kurulum için 0 gününüz kaldı" yazıyordu.

        "Kaldı" demek, kalmamışken, hiçbir şey dememekten kötüdür:
        kullanıcı hâlâ vakti olduğunu sanır.

        Geçiş süresi G-AUT-01b'de "kullanıcı hazırlansın" diye var.
        Hazırlanması gerektiği söylenmiyorsa süre bir şey ifade etmiyor —
        ve bedeli, hesabına erişimini kaybetmesi.

        İki durum ARTIK AYRI, çünkü ayrı şeyler: biri "süren var", öteki
        "süren doldu".
      */}
      {durum.gecisKalanGun !== null && durum.gecisKalanGun > 0 && (
        <p className="text-uyari-600 text-sm font-medium">
          Rolünüzde iki adımlı doğrulama zorunlu olacak. Kurulum için {durum.gecisKalanGun} gününüz
          kaldı.
        </p>
      )}

      {durum.kurulumZorunlu && (
        /*
          Sayı YOK — burada `gecisKalanGun` ya `null` ya `0` ve ikisi de
          gösterilmeye değmez. `null` "geçiş hakkı hiç olmadı" demek
          (hesap baştan zorunlu politikayla açılmış), `0` "doldu".
          İkisini de aynı cümle karşılıyor.
        */
        <p className="text-uyari-600 text-sm font-medium">
          Rolünüzde iki adımlı doğrulama zorunlu. Kurulum için tanınan süre doldu.
        </p>
      )}

      {adim === KURULUM_ADIMI.KODLAR ? (
        <YedekKodlar kodlar={tamamla.data?.yedekKodlar ?? []} />
      ) : adim === KURULUM_ADIMI.QR && baslat.data ? (
        <div className="flex flex-col gap-3">
          <p className="text-notr-700 text-sm">
            Kimlik doğrulama uygulamanızla (Google Authenticator, Authy, 1Password…) aşağıdaki kodu
            okutun, sonra uygulamadaki 6 haneli kodu yazın.
          </p>
          <img
            src={baslat.data.qrVeriUrl}
            alt="İki adımlı doğrulama QR kodu"
            className="rounded-kart border-notr-200 bg-notr-0 shadow-kart h-44 w-44 border p-2"
          />
          <div className="text-notr-700 text-sm">
            <span className="text-notr-600">QR okutamıyorsanız elle girin: </span>
            <code className="bg-notr-100 rounded px-1.5 py-0.5 font-mono text-xs">
              {baslat.data.gizliAnahtar}
            </code>
          </div>

          <Alan
            etiket="Uygulamadaki kod"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={kod}
            onChange={(e) => setKod(e.target.value)}
            hata={
              tamamla.isError
                ? tamamla.error instanceof ApiHatasi
                  ? tamamla.error.message
                  : "Kod doğrulanamadı."
                : undefined
            }
          />

          <div className="flex gap-2">
            <Buton
              yukleniyor={tamamla.isPending}
              disabled={kod.trim().length < 6}
              onClick={() => tamamla.mutate()}
            >
              Kurulumu tamamla
            </Buton>
            <Buton gorunum="sessiz" onClick={() => setAdim(KURULUM_ADIMI.KAPALI)}>
              Vazgeç
            </Buton>
          </div>
          <p className="text-notr-600 text-xs">
            Doğru kod girilene kadar kurulum tamamlanmış sayılmaz — yarıda bırakırsanız hesabınız
            eskisi gibi çalışmaya devam eder.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {baslat.isError && (
            <p role="alert" className="text-hata-500 text-sm font-medium">
              {baslat.error instanceof ApiHatasi ? baslat.error.message : "Kurulum başlatılamadı."}
            </p>
          )}
          <div>
            <Buton yukleniyor={baslat.isPending} onClick={() => baslat.mutate()}>
              Kurulumu başlat
            </Buton>
          </div>
        </div>
      )}
    </div>
  );
}

function IkiAsamaKurulu({ durum, yenile }: { durum: IkiAsamaDurumu; yenile: () => Promise<void> }) {
  const [islem, setIslem] = useState<"yok" | "kapat" | "kod">("yok");
  const [kod, setKod] = useState("");

  const kapat = useMutation({
    mutationFn: () => ikiAsamaKapat(kod.trim()),
    onSuccess: async () => {
      setIslem("yok");
      setKod("");
      await yenile();
    },
  });
  const yenileKodlar = useMutation({
    mutationFn: () => yedekKodYenile(kod.trim()),
    onSuccess: async () => {
      setKod("");
      await yenile();
    },
  });

  const calisan = islem === "kapat" ? kapat : yenileKodlar;

  if (yenileKodlar.isSuccess && islem === "kod") {
    return (
      <div className="flex flex-col gap-3">
        <YedekKodlar kodlar={yenileKodlar.data.yedekKodlar} />
        <div>
          <Buton
            gorunum="ikincil"
            onClick={() => {
              yenileKodlar.reset();
              setIslem("yok");
            }}
          >
            Kapat
          </Buton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-notr-700 text-sm">
        Girişte şifrenizden sonra uygulamadaki kod soruluyor.{" "}
        {/*
          KALAN YEDEK KOD SAYISI görünür olmak zorunda. Kullanıcı
          kodlarını harcadığını fark etmezse, telefonunu kaybettiği
          gün elinde hiçbir yol kalmadığını orada öğrenir.
        */}
        <span className={durum.kalanYedekKod <= 2 ? "text-uyari-600 font-medium" : ""}>
          {durum.kalanYedekKod} yedek kodunuz kaldı.
        </span>
      </p>

      {islem === "yok" ? (
        <div className="flex flex-wrap gap-2">
          <Buton gorunum="ikincil" onClick={() => setIslem("kod")}>
            Yedek kodları yenile
          </Buton>
          {/*
            ZORUNLU POLİTİKADA DÜĞME YOK. Sunucu 403 döndürüyor;
            düğmeyi göstermek, basılıp hata alınacak bir vaat olurdu.
          */}
          {durum.etkinPolitika !== "REQUIRED" && (
            <Buton gorunum="sessiz" onClick={() => setIslem("kapat")}>
              İki adımlı doğrulamayı kapat
            </Buton>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <Alan
            etiket={
              islem === "kapat"
                ? "Kapatmak için uygulamadaki kod"
                : "Yenilemek için uygulamadaki kod"
            }
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={kod}
            onChange={(e) => setKod(e.target.value)}
            yardim={
              islem === "kapat"
                ? "Kapatınca yedek kodlarınız da silinir — elinizdeki kâğıt çalışmaz olur."
                : "Yenileyince eski kodların hepsi geçersiz olur."
            }
            hata={
              calisan.isError
                ? calisan.error instanceof ApiHatasi
                  ? calisan.error.message
                  : "İşlem tamamlanamadı."
                : undefined
            }
          />
          <div className="flex gap-2">
            <Buton
              yukleniyor={calisan.isPending}
              disabled={kod.trim().length < 6}
              onClick={() => (islem === "kapat" ? kapat.mutate() : yenileKodlar.mutate())}
            >
              {islem === "kapat" ? "Kapat" : "Yenile"}
            </Buton>
            <Buton
              gorunum="sessiz"
              onClick={() => {
                setIslem("yok");
                setKod("");
                kapat.reset();
                yenileKodlar.reset();
              }}
            >
              Vazgeç
            </Buton>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Yedek kodlar BİR KEZ gösterilir.
 *
 * Sunucu yalnızca özetlerini saklıyor; bu ekran kapandıktan sonra
 * kodları kimse — Süper Admin dahil — bir daha gösteremez. O yüzden
 * uyarı süslü değil, düz ve büyük.
 */
function YedekKodlar({ kodlar }: { kodlar: string[] }) {
  return (
    <div className="rounded-kart shadow-kart border-uyari-500 bg-uyari-50 flex flex-col gap-2 border p-3">
      <p className="text-notr-900 text-sm font-medium">
        Bu kodları şimdi kaydedin — bir daha gösterilmeyecek.
      </p>
      <p className="text-notr-700 text-sm">
        Telefonunuza erişemediğinizde her biri bir kez giriş yapmanızı sağlar.
      </p>
      <ul className="grid grid-cols-2 gap-1.5 sm:grid-cols-5">
        {kodlar.map((k) => (
          <li
            key={k}
            className="bg-notr-0 text-notr-900 rounded px-2 py-1.5 text-center font-mono text-sm"
          >
            {k}
          </li>
        ))}
      </ul>
    </div>
  );
}

// =====================================================================
// SUDO PENCERESİ — G-SES-04
// =====================================================================

/**
 * ŞİFRE DEĞİŞTİRME — G-AUT-08a (A9, 05.10.2026).
 *
 * Bu ekranın üç ayrı yeri ("tanımadığınız bir satır görürseniz ...
 * şifrenizi değiştirin") kullanıcıyı şifre değiştirmeye yönlendiriyordu ve
 * oturum içinde bunun YOLU YOKTU — tek seçenek e-postayla sıfırlamaydı.
 * Form ortak (`@koclukpanel/ui` `SifreDegistirFormu`); asgari uzunluk
 * personelin (panel yalnız personele açık).
 */
function SifreDegistirBolumu() {
  const qc = useQueryClient();
  const [basari, setBasari] = useState<string | null>(null);
  const degistir = useMutation({
    mutationFn: sifreDegistir,
    onSuccess: async (r) => {
      setBasari(
        r.kapatilanOturum > 0
          ? `Şifreniz değiştirildi. Diğer cihazlardaki ${r.kapatilanOturum} oturum kapatıldı.`
          : "Şifreniz değiştirildi.",
      );
      await qc.invalidateQueries({ queryKey: ["oturumlarim"] });
      await qc.invalidateQueries({ queryKey: ["guvenilir-cihazlar"] });
    },
    onMutate: () => setBasari(null),
  });
  const hata = degistir.error instanceof ApiHatasi ? degistir.error : null;

  return (
    <Kart
      baslik="Şifre"
      aciklama="Mevcut şifrenizle yeni bir şifre belirleyin. Unuttuysanız giriş ekranındaki “Şifremi unuttum” bağlantısını kullanın."
    >
      <SifreDegistirFormu
        gucHesapla={(s) => sifreGucu(s, PERSONEL_EN_AZ)}
        gucAdi={GUC_ADI}
        enAz={PERSONEL_EN_AZ}
        gonderiliyor={degistir.isPending}
        hata={
          degistir.isError
            ? hata?.kod === "DOGRULAMA"
              ? null
              : (hata?.message ?? "Şifre değiştirilemedi.")
            : null
        }
        yeniSifreHatasi={
          hata?.kod === "DOGRULAMA" ? (hata.ayrinti?.yeniSifre ?? hata.message) : null
        }
        basari={basari}
        onGonder={(v) => degistir.mutate(v)}
      />
    </Kart>
  );
}

function SudoBolumu() {
  const qc = useQueryClient();
  const durum = useQuery({
    queryKey: ["sudo-durum"],
    queryFn: sudoDurum,
    retry: false,
    /*
      Pencere zamanla kapanıyor; sorgu tazelenmezse ekran "açık"
      yazmaya devam eder ve kullanıcı kapatılmış sanır. 30 sn, kalan
      süreyi dakika olarak göstermeye yetecek kadar sık.
    */
    refetchInterval: 30_000,
  });

  const kapat = useMutation({
    mutationFn: sudoKapat,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["sudo-durum"] });
    },
  });

  if (durum.isPending || durum.isError) return null;

  const dakika = Math.max(1, Math.ceil(durum.data.kalanSaniye / 60));

  return (
    <Kart
      baslik="Yetki penceresi"
      aciklama="Rol atama, entegrasyon anahtarı, korumalı PDR notu gibi işlemler için şifreniz yeniden sorulur. Onaydan sonra pencere bir süre açık kalır."
      ton={durum.data.acik ? "uyari" : "notr"}
      eylem={durum.data.acik ? <Rozet ton="uyari">Açık</Rozet> : <Rozet>Kapalı</Rozet>}
    >
      {durum.data.acik ? (
        <div className="flex flex-col gap-2">
          <p className="text-notr-700 text-sm">
            Pencere açık; yaklaşık <strong>{dakika} dakika</strong> sonra kendiliğinden kapanacak.
          </p>
          {/*
            SÜRE DOLMASINI BEKLEMEK TEK SEÇENEK OLMAMALI. Ortak bir
            bilgisayarda hassas işini bitiren kişi masadan kalkarken
            arkasında açık bir yetki penceresi bırakırdı.
          */}
          <div>
            <Buton gorunum="ikincil" yukleniyor={kapat.isPending} onClick={() => kapat.mutate()}>
              Pencereyi şimdi kapat
            </Buton>
          </div>
          {kapat.isError && (
            <p role="alert" className="text-hata-500 text-sm font-medium">
              {kapat.error instanceof ApiHatasi ? kapat.error.message : "Kapatılamadı."}
            </p>
          )}
        </div>
      ) : (
        <p className="text-notr-700 text-sm">
          Şu an açık bir yetki pencereniz yok. Hassas bir işlem yaptığınızda şifreniz sorulacak.
        </p>
      )}
    </Kart>
  );
}

// =====================================================================
// IP KISITI — G-SES-07
// =====================================================================

/**
 * ================================================================
 * BU EKRAN OLMADAN OZELLIK ERISILEMEZDI
 * ================================================================
 * `allowedIpRanges` semada bastan beri vardi ve KODDA SIFIR KULLANIM
 * olculdu (02.09.2026). Sunucu tarafi yazildi; cagiran bir ekran
 * olmasaydi bu sefer de "uc var, yol yok" olurdu.
 *
 * ================================================================
 * KILITLENME BURADA DA ANLATILIYOR
 * ================================================================
 * Sunucu kendi adresini disarida birakan listeyi REDDEDIYOR, ama
 * kullanicinin bunu denemeden once bilmesi gerekiyor. "Su anki
 * adresiniz" bilgisi bu yuzden ekranda: ne yazacagini bilmeyen kisi
 * ya cok genis yazar (kisit anlamsizlasir) ya da kendini kilitler.
 */
function IpKisitiBolumu() {
  const qc = useQueryClient();
  const [duzenle, setDuzenle] = useState(false);
  const [metin, setMetin] = useState("");
  const [sifre, setSifre] = useState("");

  const durum = useQuery({ queryKey: ["ip-kisiti"], queryFn: ipKisitiGetir, retry: false });

  const yaz = useMutation({
    mutationFn: () =>
      ipKisitiYaz({
        araliklar: metin
          .split(/[\n,]/)
          .map((a) => a.trim())
          .filter((a) => a.length > 0),
        sifre,
      }),
    onSuccess: async () => {
      setDuzenle(false);
      setSifre("");
      await qc.invalidateQueries({ queryKey: ["ip-kisiti"] });
    },
  });

  if (durum.isPending) return <Iskelet satir={2} />;
  if (durum.isError) return null;

  const acik = durum.data.araliklar.length > 0;

  return (
    <Kart
      baslik="IP kısıtı"
      aciklama="Hesabınıza yalnızca belirli ağlardan girilmesini isteyebilirsiniz. Kontrol her istekte yapılır — çalınan bir çerez bile izinli ağın dışından işe yaramaz."
      ton={acik ? "uyari" : "notr"}
      eylem={acik ? <Rozet ton="uyari">Açık</Rozet> : <Rozet>Kapalı</Rozet>}
    >
      <div className="flex flex-col gap-3">
        {acik ? (
          <ul className="flex flex-col gap-1">
            {durum.data.araliklar.map((a) => (
              <li key={a} className="text-notr-800 font-mono text-sm">
                {a}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-notr-700 text-sm">
            Kısıt yok — hesabınıza her ağdan girilebilir. Bu, özelliğin varsayılan hâli.
          </p>
        )}

        <p className="text-notr-600 text-xs">
          Şu anki adresiniz:{" "}
          <span className="font-mono">{durum.data.suAnkiIp ?? "bilinmiyor"}</span>
        </p>

        {!duzenle ? (
          <div>
            <Buton
              gorunum="ikincil"
              onClick={() => {
                setMetin(durum.data.araliklar.join("\n"));
                setDuzenle(true);
              }}
            >
              {acik ? "Listeyi düzenle" : "Kısıt ekle"}
            </Buton>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <MetinAlani
              etiket="İzinli adresler"
              rows={4}
              value={metin}
              onChange={(e) => setMetin(e.target.value)}
              placeholder={"203.0.113.7\n203.0.113.0/24\n2001:db8::/32"}
              yardim="Her satıra bir adres ya da aralık. IPv4 ve IPv6 desteklenir. BOŞ bırakırsanız kısıt kalkar."
            />

            {/*
              KENDI ADRESINI EKLEMEK TEK TIKLA. Elle yazdirmak, bir
              harf hatasinin kullaniciyi kilitlemesi demek — sunucu
              reddediyor ama kullanici sebebini anlamiyor.
            */}
            {durum.data.suAnkiIp && (
              <div>
                <Buton
                  gorunum="sessiz"
                  onClick={() =>
                    setMetin((m) =>
                      m.includes(durum.data.suAnkiIp!)
                        ? m
                        : (m.trim() ? m.trimEnd() + "\n" : "") + durum.data.suAnkiIp,
                    )
                  }
                >
                  Şu anki adresimi ekle
                </Buton>
              </div>
            )}

            <Alan
              etiket="Şifreniz"
              type="password"
              value={sifre}
              onChange={(e) => setSifre(e.target.value)}
              yardim="Güvenlik ayarı değiştiriliyor; şifre yeniden isteniyor."
            />

            {yaz.isError && (
              <p role="alert" className="text-hata-500 text-sm font-medium">
                {yaz.error instanceof ApiHatasi ? yaz.error.message : "Kaydedilemedi."}
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <Buton
                yukleniyor={yaz.isPending}
                disabled={sifre.length === 0}
                onClick={() => yaz.mutate()}
              >
                Kaydet
              </Buton>
              <Buton
                gorunum="ikincil"
                onClick={() => {
                  setDuzenle(false);
                  setSifre("");
                }}
              >
                Vazgeç
              </Buton>
            </div>
          </div>
        )}
      </div>
    </Kart>
  );
}

// =====================================================================
// PROFİL GÖRSELİ — F-PPL-13
// =====================================================================

/**
 * ================================================================
 * BU BÖLÜM 04.09.2026'DA YAZILDI
 * ================================================================
 * Şemada `avatarUrl`/`avatarPreset` ve `AVATAR` dosya türü baştan
 * beri vardı; şartname maddesi 28.08.2026'da eklendi, arayüzü hiç
 * yazılmadı. Kuralı olmayan kişisel veri alanı bırakılmaz.
 *
 * ================================================================
 * FOTOĞRAF YÜKLEME 07.09.2026'DA AÇILDI
 * ================================================================
 * Şartnamenin ikinci yolu. 04.09.2026'da bilerek yazılmamıştı ve
 * sebebi ölçülmüştü: dosya altyapısı öğrenci merkezli (kota öğrenci
 * başına), personel avatarı o yola oturmuyordu.
 *
 * Emre'nin kararı: fotoğraf açılsın, kota KİŞİ BAŞINA. Kural ayrı bir
 * kota sahibiyle çözüldü; doğrulama kuralları (boyut, MIME, içerik
 * imzası) `dosya-kural.ts` içinde paylaşılıyor.
 *
 * ================================================================
 * GÖRSEL YOKSA BAŞ HARFLER — BOŞ ÇERÇEVE ÇİZİLMİYOR
 * ================================================================
 * Şartnamenin kendi cümlesi. Boş çerçeve, yüklenmemiş bir görsel ile
 * yüklenememiş bir görseli aynı gösterirdi.
 */
function ProfilGorseli() {
  const qc = useQueryClient();

  const gorsel = useQuery({ queryKey: ["avatarim"], queryFn: avatarim, retry: false });
  const hazirlar = useQuery({
    queryKey: ["hazir-avatar-sayisi"],
    queryFn: hazirAvatarSayisi,
    retry: false,
  });

  const sec = useMutation({
    mutationFn: (secim: number | null) => hazirAvatarSec(secim),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["avatarim"] });
    },
  });

  const yukle = useMutation({
    mutationFn: (dosya: File) => avatarFotoYukle(dosya),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["avatarim"] });
      await qc.invalidateQueries({ queryKey: ["avatar-foto"] });
    },
  });

  if (gorsel.isPending) return <Iskelet satir={3} />;
  if (gorsel.isError) return null;

  const adet = hazirlar.data?.adet ?? 0;

  return (
    <Kart
      baslik="Profil görseli"
      aciklama="Yalnızca siz değiştirebilirsiniz; yönetici de değiştiremez, yalnızca uygunsuz bir görseli kaldırabilir."
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <Onizleme gorsel={gorsel.data} />
          <div className="text-notr-700 text-sm">
            {gorsel.data.fotoVarMi ? (
              <p>Yüklenmiş bir fotoğrafınız var.</p>
            ) : gorsel.data.avatarPreset !== null ? (
              <p>
                <strong>{hazirAvatarAdi(gorsel.data.avatarPreset)}</strong> avatarı seçili.
              </p>
            ) : (
              <p>Görsel seçilmedi; baş harfleriniz gösteriliyor. Boş çerçeve çizilmiyor.</p>
            )}
          </div>
        </div>

        {adet > 0 && (
          <fieldset className="flex flex-col gap-2">
            <legend className="text-notr-800 text-sm font-medium">Hazır avatarlar</legend>
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: adet }, (_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`${hazirAvatarAdi(i)} avatarını seç`}
                  aria-pressed={gorsel.data.avatarPreset === i}
                  disabled={sec.isPending}
                  onClick={() => sec.mutate(i)}
                  className={cn(
                    "min-h-dokunma min-w-dokunma flex items-center justify-center rounded-full p-1",
                    gorsel.data.avatarPreset === i
                      ? "bg-marka-600 text-notr-0 ring-marka-600 ring-2 ring-offset-2"
                      : "bg-notr-100 text-notr-700 hover:bg-notr-200",
                  )}
                >
                  <HazirAvatar secim={i} boyut={40} aria-hidden />
                </button>
              ))}
            </div>
          </fieldset>
        )}

        {sec.isError && (
          <p role="alert" className="text-hata-700 text-sm font-medium">
            {sec.error instanceof ApiHatasi ? sec.error.message : "Seçim kaydedilemedi."}
          </p>
        )}

        {/*
          ================================================================
          FOTOĞRAF YÜKLEME — F-PPL-13, ikinci yol
          ================================================================
          Ham `<input type="file">`: `Alan` DENETİMLİ bir alan
          (`value` + `onChange`) ve dosya alanının `value`si GÜVENLİK
          GEREĞİ koddan atanamıyor — tarayıcı izin vermiyor. `Alan`a
          sokmak, çalışmayan bir sözleşme uydurmak olurdu. Muafiyet
          `form-bileseni.test.ts` içinde yazılı ve biçim kurallarını
          KALDIRMIYOR: 44px dokunma hedefi ve etiket bağı burada elle
          sağlanıyor.
        */}
        <div className="flex flex-col gap-2">
          <label htmlFor="avatar-foto" className="text-notr-800 text-sm font-medium">
            Kendi fotoğrafın
          </label>
          <input
            id="avatar-foto"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={yukle.isPending}
            onChange={(e) => {
              const d = e.target.files?.[0];
              /*
                Alan SEÇİMDEN SONRA temizleniyor: aynı dosya ikinci kez
                seçildiğinde `change` olayı hiç tetiklenmez ve kullanıcı
                "bir şey olmadı" görürdü.
              */
              e.target.value = "";
              if (d) yukle.mutate(d);
            }}
            className="min-h-dokunma text-notr-700 file:rounded-alan file:border-notr-200 file:bg-notr-50 file:text-notr-800 hover:file:bg-notr-100 text-sm file:mr-3 file:border file:px-3 file:py-2 file:text-sm"
          />
          <p className="text-notr-600 text-xs">
            JPEG, PNG ya da WebP · en fazla 2 MB. Yeni fotoğraf eskisinin yerine geçer; hazır avatar
            seçiliyse düşer.
          </p>
        </div>

        {yukle.isError && (
          <p role="alert" className="text-hata-700 text-sm font-medium">
            {yukle.error instanceof ApiHatasi ? yukle.error.message : "Fotoğraf yüklenemedi."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {(gorsel.data.avatarPreset !== null || gorsel.data.fotoVarMi) && (
            <Buton gorunum="ikincil" yukleniyor={sec.isPending} onClick={() => sec.mutate(null)}>
              Görseli kaldır
            </Buton>
          )}
        </div>
      </div>
    </Kart>
  );
}

/**
 * Görselin küçük önizlemesi — üç hâl.
 *
 * ================================================================
 * FOTOĞRAF AYRI BİR İSTEKLE GELİYOR
 * ================================================================
 * Görünüm yalnızca "fotoğraf var mı" diyor; imzalı bağlantı ayrı
 * uçtan alınıyor ve 15 dakika yaşıyor. Bağlantıyı görünümün içinde
 * döndürseydik, otuz kişilik bir listede otuz gereksiz imza üretilir
 * ve çoğu hiç kullanılmazdı.
 *
 * Bağlantı ALINAMAZSA baş harflere düşülüyor — kırık bir resim
 * simgesi göstermek, görselin olmadığını değil sistemin bozuk
 * olduğunu söylerdi.
 */
function Onizleme({ gorsel }: { gorsel: AvatarGorunumu }) {
  const foto = useQuery({
    queryKey: ["avatar-foto"],
    queryFn: avatarimFotoBaglantisi,
    enabled: gorsel.fotoVarMi,
    retry: false,
    /*
      Bağlantı 15 dakika yaşıyor; önbellek 10 dakikada bayatlıyor ki
      ekranda süresi dolmuş bir bağlantı kalmasın.
    */
    staleTime: 10 * 60 * 1000,
  });

  if (gorsel.fotoVarMi && foto.data) {
    return <img src={foto.data.url} alt="" className="size-16 rounded-full object-cover" />;
  }

  if (gorsel.avatarPreset !== null) {
    return <HazirAvatar secim={gorsel.avatarPreset} boyut={64} />;
  }

  return (
    <div className="bg-notr-200 text-notr-700 flex size-16 items-center justify-center rounded-full text-xl font-semibold">
      {/*
        Baş harf de yoksa (ad ve soyad boş) tire gösteriliyor.
        Boş bir daire "yükleniyor" gibi okunurdu.
      */}
      {gorsel.basHarfler || "—"}
    </div>
  );
}
